// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import PropTypes from 'prop-types';

// Data
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";

// API/MODELS
import bksAPI from "services/api/bks";

// Mapbox
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';

// helper
import { useSoftUIController } from "hooks/context";
import { processDevicesData } from "utils/device";
import { disableMap } from "utils/map";
import { loadingScreen } from "utils/wrapper";
import { wrapper } from "utils/wrapper";
import { processUptimeCounts } from "utils/device";
import SoftButton from "components/SoftComponents/SoftButton";
import { getUserAccessLevel } from "utils/setup";
import { Link } from "react-router-dom";
import { SyncLoader } from "react-spinners";

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function landingStyles(theme, ownerState) {
    const { palette, typography, boxShadows, functions, transitions, breakpoints, borders } = theme;
    const { contentType } = ownerState;

    if (contentType == 'title') {
        return {
            color: '#fff',
            lineHeight: '1.3',

            [breakpoints.up('xxs')]: {
                fontSize: '36px',
            },
            [breakpoints.up('lg')]: {
                fontSize: '44px',
            },
            [breakpoints.up('xl')]: {
                fontSize: '52px',
            },
            [breakpoints.up('xxl')]: {
                fontSize: '60px',
            },
        }
    }
    else if (contentType == 'counter') {
        return {
            color: '#fff',
            [breakpoints.up('xxs')]: {
                fontSize: '20px',

                lineHeight: '2.5',
            },
            [breakpoints.up('lg')]: {
                fontSize: '28px',
                lineHeight: '2.5',
            },
            [breakpoints.up('xl')]: {
                fontSize: '32px',
                lineHeight: '2',
            },
            [breakpoints.up('xxl')]: {
                fontSize: '36px',
                lineHeight: '2',
            }
        }
    }
    else if (contentType == 'buttons') {
        return {
            [breakpoints.up('xxs')]: {
                fontSize: '13px',
            },
            [breakpoints.up('lg')]: {
                fontSize: '16px',
            },
            [breakpoints.up('xl')]: {
                fontSize: '18px',
            },
            [breakpoints.up('xxl')]: {
                fontSize: '20px',
            },

        }
    }
}

function LandingView({ map, user }) {
    disableMap(map);

    bksAPI.loadToken(useAuth0());
    const [controller] = useSoftUIController();
    const { deviceLayout } = controller;

    const devices = useQuery({
        queryKey: ['getAllDevices'],
        queryFn: async () => {
            return await bksAPI.getAllDevices()
        }
    });

    if (devices.error) {
        var error = devices.error;
        return ("An error has occurred: " + error.message);
    }

    const devicesData = devices.data ? processDevicesData(devices.data, getUserAccessLevel(user.user.user_type, 'technician')) : [];
    const uptime = devices.data ? processUptimeCounts(devicesData) : { uptime_status: [0], count: 0 };

    return wrapper((
        <SoftBox borderRadius="20px" color={"white"} width="100%"
            height={deviceLayout == 'mobile' ? 'unset' : "calc(100svh - 5.5rem - 16px)"}
            minHeight={deviceLayout == 'mobile' ? "calc(100svh - 4.25rem - 8px)" : "fit-content"}
            display="flex" flexDirection="column" justifyContent="center" alignItems={{ xxs: "center", sm: "flex-start" }}
            position="relative"
        >
            {devices.isLoading && (
                <SoftBox
                    textAlign={{ xxs: 'center', sm: 'left' }}
                    mt={deviceLayout == 'mobile' ? '5.25rem' : '6.5rem'}
                    ml={{ xxs: 0, sm: '10%' }}
                >
                    <SyncLoader color="#fff" />
                </SoftBox>
            )}
            <SoftBox
                textAlign={{ xxs: 'center', sm: 'left' }}
                mb={deviceLayout == 'mobile' ? '4.25rem' : '5.5rem'}
                ml={{ xxs: 0, sm: '10%' }}
                sx={{
                    opacity: devices.isLoading ? 0 : 1,
                    visibility: devices.isLoading ? 'hidden' : 'visible',
                    transition: 'opacity 500ms ease-in-out, visibility 500ms ease-in-out'
                }}
            >
                <SoftTypography fontWeight="bold" sx={(theme) => landingStyles(theme, { contentType: 'title' })}>Welcome back{', ' + user?.user?.nickname.charAt(0).toUpperCase() + user?.user?.nickname.slice(1)}.</SoftTypography>
                <SoftTypography fontWeight="bold" sx={(theme) => landingStyles(theme, { contentType: 'counter' })}>{uptime.uptime_status[0]} / {uptime.count} Devices online.</SoftTypography>
                <SoftBox display="flex" flexDirection="row" justifyContent={{ xxs: 'center', sm: 'flex-start' }} mt={1}>
                    <SoftBox mr={1}>
                        <SoftButton size="small" sx={{ borderRadius: '20px' }} component={Link} to={'/devices'}>
                            <SoftTypography variant="caption" sx={(theme) => landingStyles(theme, { contentType: 'buttons' })}>View All Devices</SoftTypography>
                        </SoftButton>
                    </SoftBox>
                    {getUserAccessLevel(user.user.user_type, "technician") && (
                        <SoftBox ml={1}>
                            <SoftButton size="small" sx={{ borderRadius: '20px' }} component={Link} to={'/operations/assignments'}>
                                <SoftTypography variant="caption" sx={(theme) => landingStyles(theme, { contentType: 'buttons' })}>View Assignments</SoftTypography>
                            </SoftButton>
                        </SoftBox>
                    )}
                </SoftBox>
            </SoftBox>
        </SoftBox>
    ), deviceLayout
    );
}

LandingView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default LandingView;